import {
  AppointmentTypeV3EnumDto,
  BasePayInputTypeV3EnumDto,
  CarCalculationMethodV3EnumDto,
  CreditorClaimCalculationMethodV3EnumDto,
  CreditorClaimTypeV3EnumDto,
  EarningSpecialConditionV3EnumDto,
  EmailTypeV3EnumDto,
  EmploymentTypeV3EnumDto,
  ExtraPaymentCalculationMethodV3EnumDto,
  ForeignGovernmentIdTypeV3EnumDto,
  GenderV3EnumDto,
  GovernmentIdTypeV3EnumDto,
  OperationStatusEnum,
  OperationTypeEnum,
  PaymentTypeV3EnumDto,
  PersonTypeV3EnumDto,
  PhoneTypeV3EnumDto,
  PositionEndReasonV3EnumDto,
  SalaryTypeV3EnumDto,
  SetOfAccountV3EnumDto,
  ShipRegisterV3EnumDto,
  ShippingAreaV3EnumDto,
  TaskStatusEnum,
  VesselTypeV3EnumDto,
} from '@data-access/bulk-operations-api';
import { EmployeeStatusEnum } from '@item-cache';

import { TaxAndContributionsRulesEnum } from '../enums/tax-and-contributions-rules.enum';

/**
 * Helper class for localizing enum values across the application.
 * Provides translation methods for all enum types used in the system.
 */
export class EnumLocalizationHelper {
  static getAppointmentTypeV3EnumDto(appointmentType: AppointmentTypeV3EnumDto): string {
    switch (appointmentType) {
      case AppointmentTypeV3EnumDto.Temporary:
        return $localize`Temporary`;
      case AppointmentTypeV3EnumDto.Permanent:
        return $localize`Permanent`;
      case AppointmentTypeV3EnumDto.PermanentHiredOut:
        return $localize`Permanent employment and hired out (valid from 01.01.2025)`;
      case AppointmentTypeV3EnumDto.TemporaryHiredOut:
        return $localize`Temporary employment and hired out (valid from 01.01.2025)`;
      case AppointmentTypeV3EnumDto.TemporaryOnCall:
        return $localize`Temporary employment as on-call coverstaff (valid from 01.01.2025)`;
      default:
        if (!appointmentType) return '';
        console.warn(`Missing translation for AppointmentTypeV3EnumDto.${appointmentType}`);
        return appointmentType;
    }
  }

  static getBasePayInputTypeV3EnumDto(basePayType: BasePayInputTypeV3EnumDto): string {
    switch (basePayType) {
      case BasePayInputTypeV3EnumDto.Hourly:
        return $localize`Hourly`;
      case BasePayInputTypeV3EnumDto.FullTimeMonthly:
        return $localize`Monthly`;
      case BasePayInputTypeV3EnumDto.FullTimeYearly:
        return $localize`Yearly`;
      default:
        if (!basePayType) return '';
        console.warn(`Missing translation for BasePayInputTypeV3EnumDto.${basePayType}`);
        return basePayType;
    }
  }

  static getCarCalculationMethodV3EnumDto(
    carCalculationMethod: CarCalculationMethodV3EnumDto,
  ): string {
    switch (carCalculationMethod) {
      case CarCalculationMethodV3EnumDto.Kilometers:
        return $localize`Kilometers`;
      case CarCalculationMethodV3EnumDto.ListPrice:
        return $localize`List price`;
      default:
        if (!carCalculationMethod) return '';
        console.warn(
          `Missing translation for CarCalculationMethodV3EnumDto.${carCalculationMethod}`,
        );
        return carCalculationMethod;
    }
  }

  static getCreditorClaimCalculationMethodV3EnumDto(
    calculationMethod: CreditorClaimCalculationMethodV3EnumDto,
  ): string {
    switch (calculationMethod) {
      case CreditorClaimCalculationMethodV3EnumDto.FixedAmount:
        return $localize`Fixed amount`;
      case CreditorClaimCalculationMethodV3EnumDto.TaxBasisPercentage:
        return $localize`Percentage`;
      default:
        if (!calculationMethod) return '';
        console.warn(
          `Missing translation for CreditorClaimCalculationMethodV3EnumDto.${calculationMethod}`,
        );
        return calculationMethod;
    }
  }

  static getCreditorClaimTypeV3EnumDto(creditorClaimType: CreditorClaimTypeV3EnumDto): string {
    switch (creditorClaimType) {
      case CreditorClaimTypeV3EnumDto.ChildSupport:
        return $localize`Child support`;
      case CreditorClaimTypeV3EnumDto.LegalOffence:
        return $localize`Compensation legal offence`;
      case CreditorClaimTypeV3EnumDto.Coordinated:
        return $localize`Coordinated attachment of earnings`;
      case CreditorClaimTypeV3EnumDto.Tax:
        return $localize`Unpaid tax`;
      case CreditorClaimTypeV3EnumDto.GovernmentClaim:
        return $localize`Claims from State-owned Company`;
      case CreditorClaimTypeV3EnumDto.UserDefined:
        return $localize`Other Creditor Claim Type`;
      default:
        if (!creditorClaimType) return '';
        console.warn(`Missing translation for CreditorClaimTypeV3EnumDto.${creditorClaimType}`);
        return creditorClaimType;
    }
  }

  static getEmailTypeV3EnumDto(emailType: EmailTypeV3EnumDto): string {
    switch (emailType) {
      case EmailTypeV3EnumDto.Private:
        return $localize`Private`;
      case EmailTypeV3EnumDto.Work:
        return $localize`Business`;
      default:
        if (!emailType) return '';
        console.warn(`Missing translation for EmailTypeV3EnumDto.${emailType}`);
        return emailType;
    }
  }

  static getEarningSpecialConditionV3EnumDto(
    earningSpecialCondition: EarningSpecialConditionV3EnumDto,
  ): string {
    switch (earningSpecialCondition) {
      case EarningSpecialConditionV3EnumDto.ChildUnder13:
        return $localize`Child under 13`;
      case EarningSpecialConditionV3EnumDto.Seafarer:
        return $localize`Seafarer`;
      case EarningSpecialConditionV3EnumDto.NonTaxable:
        return $localize`Non-taxable`;
      case EarningSpecialConditionV3EnumDto.LabourMarketScheme:
        return $localize`Labour market scheme`;
      case EarningSpecialConditionV3EnumDto.WageGuarantee:
        return $localize`Wage guarantee`;
      case EarningSpecialConditionV3EnumDto.ForeignSeafarer:
        return $localize`Foreign seafarer`;
      case EarningSpecialConditionV3EnumDto.OceanMineralRenewableCarbon:
        return $localize`Ocean mineral renewable carbon`;
      default:
        if (!earningSpecialCondition) return '';
        console.warn(
          `Missing translation for EarningSpecialConditionV3EnumDto.${earningSpecialCondition}`,
        );
        return earningSpecialCondition;
    }
  }

  static getEmployeeStatusEnum(value: EmployeeStatusEnum): string {
    switch (value) {
      case EmployeeStatusEnum.Active:
        return $localize`Active`;
      case EmployeeStatusEnum.Inactive:
        return $localize`Inactive`;
      case EmployeeStatusEnum.Future:
        return $localize`Future`;
      case EmployeeStatusEnum.Draft:
        return $localize`Draft`;
      default:
        if (!value) return '';
        console.warn(`Missing translation for EmployeeStatusEnum.${value}`);
        return value;
    }
  }

  static getEmploymentTypeV3EnumDto(employmentType: EmploymentTypeV3EnumDto): string {
    switch (employmentType) {
      case EmploymentTypeV3EnumDto.Ordinary:
        return $localize`Ordinary`;
      case EmploymentTypeV3EnumDto.Freelancer:
        return $localize`Freelancer`;
      case EmploymentTypeV3EnumDto.Pension:
        return $localize`Pension and other renumerations`;
      case EmploymentTypeV3EnumDto.Maritime:
        return $localize`Maritime`;
      case EmploymentTypeV3EnumDto.PermanentlyAdaptedWork:
        return $localize`PAW / without employment`;
      default:
        if (!employmentType) return '';
        console.warn(`Missing translation for EmploymentTypeV3EnumDto.${employmentType}`);
        return employmentType;
    }
  }

  static getExtraPaymentCalculationMethodV3EnumDto(
    extraPaymentCalcMethod: ExtraPaymentCalculationMethodV3EnumDto,
  ): string {
    switch (extraPaymentCalcMethod) {
      case ExtraPaymentCalculationMethodV3EnumDto.FixedAmount:
        return $localize`Fixed Amount`;
      case ExtraPaymentCalculationMethodV3EnumDto.Paycode:
        return $localize`Paycode`;
      default:
        if (!extraPaymentCalcMethod) return '';
        console.warn(
          `Missing translation for ExtraPaymentCalculationMethodV3EnumDto.${extraPaymentCalcMethod}`,
        );
        return extraPaymentCalcMethod;
    }
  }

  static getForeignGovernmentIdTypeV3EnumDto(
    governmentIdType: ForeignGovernmentIdTypeV3EnumDto,
  ): string {
    switch (governmentIdType) {
      case ForeignGovernmentIdTypeV3EnumDto.Passport:
        return $localize`Passport`;
      case ForeignGovernmentIdTypeV3EnumDto.SocialSecurityNumber:
        return $localize`Social security number`;
      case ForeignGovernmentIdTypeV3EnumDto.TaxNumber:
        return $localize`Tax number`;
      case ForeignGovernmentIdTypeV3EnumDto.ValueAddedTaxNumber:
        return $localize`Value added tax number`;
      default:
        if (!governmentIdType) return '';
        console.warn(
          `Missing translation for ForeignGovernmentIdTypeV3EnumDto.${governmentIdType}`,
        );
        return governmentIdType;
    }
  }

  static getGenderV3EnumDto(gender: GenderV3EnumDto): string {
    switch (gender) {
      case GenderV3EnumDto.Female:
        return $localize`Female`;
      case GenderV3EnumDto.Male:
        return $localize`Male`;
      default:
        if (!gender) return '';
        console.warn(`Missing translation for GenderV3EnumDto.${gender}`);
        return gender;
    }
  }

  static getGovernmentIdTypeV3EnumDto(governmentIdType: GovernmentIdTypeV3EnumDto): string {
    switch (governmentIdType) {
      case GovernmentIdTypeV3EnumDto.Foreign:
        return $localize`International ID`;
      case GovernmentIdTypeV3EnumDto.DNumber:
        return $localize`D-number`;
      case GovernmentIdTypeV3EnumDto.National:
        return $localize`National ID`;
      default:
        if (!governmentIdType) return '';
        console.warn(`Missing translation for GovernmentIdTypeV3EnumDto.${governmentIdType}`);
        return governmentIdType;
    }
  }

  static getOperationStatusEnum(status: OperationStatusEnum): string {
    switch (status) {
      case OperationStatusEnum.Successful:
        return $localize`Successful`;
      case OperationStatusEnum.Incomplete:
        return $localize`Incomplete`;
      case OperationStatusEnum.InProgress:
        return $localize`In Progress`;
      default:
        if (!status) return '';
        console.warn(`Missing translation for OperationStatusEnum.${status}`);
        return status;
    }
  }

  static getOperationTypeEnum(value: OperationTypeEnum): string {
    switch (value) {
      case OperationTypeEnum.AbsenceRegistrationImport:
        return $localize`Absences`;
      case OperationTypeEnum.VacationBalanceImport:
        return $localize`Vacation balances`;
      case OperationTypeEnum.SalaryUpdateV2:
        return $localize`Salary update`;
      case OperationTypeEnum.EndFixedTransactions:
        return $localize`End fixed transactions`;
      case OperationTypeEnum.ChangeTimeAgreements:
        return $localize`Time agreements`;
      case OperationTypeEnum.PositionTagsUpdate:
        return $localize`Position tags update`;
      case OperationTypeEnum.EndPositions:
        return $localize`End positions`;
      case OperationTypeEnum.CostUnitValueUpsert:
        return $localize`Cost unit edit`;
      case OperationTypeEnum.AssignUser:
        return $localize`Create users`;
      case OperationTypeEnum.ChangeManager:
        return $localize`Change managers`;
      case OperationTypeEnum.EditFixedTransactions:
        return $localize`Edit fixed transactions`;
      case OperationTypeEnum.PayRateUpdate:
        return $localize`Edit rates`;
      case OperationTypeEnum.ManageCostUnitValues:
        return $localize`Change cost units`;
      default:
        if (!value) return '';
        console.warn(`Missing translation for OperationTypeEnum.${value}`);
        return value;
    }
  }

  static getPaymentTypeV3EnumDto(paymentType: PaymentTypeV3EnumDto): string {
    switch (paymentType) {
      case PaymentTypeV3EnumDto.NorwegianBank:
        return $localize`Norwegian Bank`;
      case PaymentTypeV3EnumDto.ForeignBank:
        return $localize`Foreign Bank`;
      case PaymentTypeV3EnumDto.External:
        return $localize`External`;
      default:
        if (!paymentType) return '';
        console.warn(`Missing translation for PaymentTypeV3EnumDto.${paymentType}`);
        return paymentType;
    }
  }

  static getPersonTypeV3EnumDto(personType: PersonTypeV3EnumDto): string {
    switch (personType) {
      case PersonTypeV3EnumDto.CommuterNorwegian:
        return $localize`Commuter Norwegian`;
      case PersonTypeV3EnumDto.CommuterForeign:
        return $localize`Commuter Foreign`;
      case PersonTypeV3EnumDto.CommuterForeignTaxCardNoDeduction:
        return $localize`Commuter Foreign Tax Card No Deduction`;
      case PersonTypeV3EnumDto.ExpatriateInNorway:
        return $localize`Expatriate In Norway`;
      case PersonTypeV3EnumDto.OffshoreWorker:
        return $localize`Offshore Worker`;
      default:
        if (!personType) return '';
        console.warn(`Missing translation for PersonTypeV3EnumDto.${personType}`);
        return personType;
    }
  }

  static getPhoneTypeV3EnumDto(phoneType: PhoneTypeV3EnumDto): string {
    switch (phoneType) {
      case PhoneTypeV3EnumDto.Private:
        return $localize`Private`;
      case PhoneTypeV3EnumDto.Work:
        return $localize`Business`;
      default:
        if (!phoneType) return '';
        console.warn(`Missing translation for PhoneTypeV3EnumDto.${phoneType}`);
        return phoneType;
    }
  }

  static getPositionEndReasonV3EnumDto(endReason: PositionEndReasonV3EnumDto): string {
    switch (endReason) {
      case PositionEndReasonV3EnumDto.DataEntryMistake:
        return $localize`The employment should never have been reported`;
      case PositionEndReasonV3EnumDto.EmployeeResignation:
        return $localize`The employee has resigned from his/her position`;
      case PositionEndReasonV3EnumDto.EmployerTermination:
        return $localize`The employer has terminated the employment relationship`;
      case PositionEndReasonV3EnumDto.HiringTermsExpiration:
        return $localize`Contract, temporary appointment or temporary position has been terminated`;
      case PositionEndReasonV3EnumDto.Organizational:
        return $localize`Changes in organisational structure or internal job swap`;
      case PositionEndReasonV3EnumDto.Technical:
        return $localize`Changed payroll system or accountant`;
      default:
        if (!endReason) return '';
        console.warn(`Missing translation for PositionEndReasonV3EnumDto.${endReason}`);
        return endReason;
    }
  }

  static getSalaryTypeV3EnumDto(salaryType: SalaryTypeV3EnumDto): string {
    switch (salaryType) {
      case SalaryTypeV3EnumDto.Hourly:
        return $localize`Variable salary`;
      case SalaryTypeV3EnumDto.Fixed:
        return $localize`Fixed salary`;
      case SalaryTypeV3EnumDto.Commission:
        return $localize`Commission / Percentage (to 31.12.2018)`;
      case SalaryTypeV3EnumDto.Honorarium:
        return $localize`Fee (to 31.12.2018)`;
      case SalaryTypeV3EnumDto.StraightPieceWork:
        return $localize`Payment by results (to 31.12.2018)`;
      default:
        if (!salaryType) return '';
        console.warn(`Missing translation for SalaryTypeV3EnumDto.${salaryType}`);
        return salaryType;
    }
  }

  static getSetOfAccountV3EnumDto(setOfAccount: SetOfAccountV3EnumDto): string {
    switch (setOfAccount) {
      case SetOfAccountV3EnumDto.BlueCollar:
        return $localize`Blue collar`;
      case SetOfAccountV3EnumDto.WhiteCollar:
        return $localize`White collar`;
      case SetOfAccountV3EnumDto.Other:
        return $localize`Other Set Of Account`;
      default:
        if (!setOfAccount) return '';
        console.warn(`Missing translation for SetOfAccountV3EnumDto.${setOfAccount}`);
        return setOfAccount;
    }
  }

  static getShipRegisterV3EnumDto(shipRegister: ShipRegisterV3EnumDto): string {
    switch (shipRegister) {
      case ShipRegisterV3EnumDto.Norwegian:
        return $localize`Norwegian`;
      case ShipRegisterV3EnumDto.NorwegianInternational:
        return $localize`Norwegian International`;
      case ShipRegisterV3EnumDto.Foreign:
        return $localize`Foreign`;
      default:
        if (!shipRegister) return '';
        console.warn(`Missing translation for ShipRegisterV3EnumDto.${shipRegister}`);
        return shipRegister;
    }
  }

  static getShippingAreaV3EnumDto(shippingArea: ShippingAreaV3EnumDto): string {
    switch (shippingArea) {
      case ShippingAreaV3EnumDto.Domestic:
        return $localize`Domestic`;
      case ShippingAreaV3EnumDto.International:
        return $localize`International`;
      default:
        if (!shippingArea) return '';
        console.warn(`Missing translation for ShippingAreaV3EnumDto.${shippingArea}`);
        return shippingArea;
    }
  }

  static getTaskStatusEnum(status: TaskStatusEnum): string {
    switch (status) {
      case TaskStatusEnum.Successful:
        return $localize`Successful`;
      case TaskStatusEnum.Created:
        return $localize`Created`;
      case TaskStatusEnum.Failed:
        return $localize`Failed`;
      case TaskStatusEnum.Waiting:
        return $localize`Waiting`;
      default:
        if (!status) return '';
        console.warn(`Missing translation for TaskStatusEnum.${status}`);
        return status;
    }
  }

  static getTaxAndContributionsRulesEnum(
    taxAndContributionRules: TaxAndContributionsRulesEnum,
  ): string {
    switch (taxAndContributionRules) {
      case TaxAndContributionsRulesEnum.SvalbardTaxAct:
        return $localize`Svalbard`;
      case TaxAndContributionsRulesEnum.SeafarerDeduction:
        return $localize`Special allowance for seafarers`;
      case TaxAndContributionsRulesEnum.NetSalary:
        return $localize`Net salary`;
      case TaxAndContributionsRulesEnum.NetSalaryForSeafarers:
        return $localize`Net salary for seafarers`;
      default:
        if (!taxAndContributionRules) return '';
        console.warn(
          `Missing translation for TaxAndContributionsRulesEnum.${taxAndContributionRules}`,
        );
        return taxAndContributionRules;
    }
  }

  static getVesselTypeV3EnumDto(vesselType: VesselTypeV3EnumDto): string {
    switch (vesselType) {
      case VesselTypeV3EnumDto.Tourist:
        return $localize`Tourist`;
      case VesselTypeV3EnumDto.DrillingPlatform:
        return $localize`Drilling Platform`;
      case VesselTypeV3EnumDto.Other:
        return $localize`Other Type Of Vessel`;
      default:
        if (!vesselType) return '';
        console.warn(`Missing translation for VesselTypeV3EnumDto.${vesselType}`);
        return vesselType;
    }
  }
}

/**
 * Union type of all enum types that can be localized
 */
export type LocalizableEnumType = {
  [K in keyof typeof EnumLocalizationHelper]: (typeof EnumLocalizationHelper)[K] extends (
    ...args: infer P
  ) => unknown
    ? P[0]
    : never;
}[keyof typeof EnumLocalizationHelper];

/**
 * String literal union type of all enum type names (method names without 'get' prefix)
 */
export type EnumTypeName = keyof {
  [K in keyof typeof EnumLocalizationHelper as K extends `get${infer R}`
    ? `${R}`
    : never]: (typeof EnumLocalizationHelper)[K];
};
